import React, { useState, useEffect, useMemo } from "react";
import {
  fetchAllNumberingConfigs,
  updateNumberingConfig,
  resetNumberingConfig,
} from "../../api/numberingApi";

const documentTypes = [
  "Invoice",
  "Booking",
  "Credit Note",
  "Estimation",
  "Sales Order",
  "Tripsheet",
  // Add more as needed (Purchase, Fuel Indent, Receipt, Payment ...)
];

const POLICIES = [
  { value: "YEARLY", label: "Restart every FY", hint: "Number restarts at 1 on 1 April" },
  { value: "CONTINUE", label: "Continue + new year", hint: "Number carries on, year in suffix changes" },
  { value: "NEVER", label: "Continue, no year", hint: "One running series, suffix never changes" },
];

const GST_LIMIT_TYPES = ["Invoice", "Credit Note"]; // GST: max 16 characters

// Financial year start year for today (Apr–Mar)
const currentFyStartYear = () => {
  const d = new Date();
  return d.getMonth() >= 3 ? d.getFullYear() : d.getFullYear() - 1;
};

const buildNumber = (cfg, fyYear, numberOverride) => {
  const policy = cfg.reset_policy || "YEARLY";
  const num = String(numberOverride ?? cfg.next_number ?? 1).padStart(
    cfg.min_length || 0,
    "0"
  );
  const suffix = policy === "NEVER" ? cfg.suffix || "" : `-${fyYear}`;
  const series =
    cfg.series > 1 ? String.fromCharCode(64 + Number(cfg.series)) : "";
  return `${cfg.prefix || ""}${num}${suffix}${series}`;
};

const emptyConfig = (dt) => ({
  document_type: dt,
  prefix: "",
  suffix: "",
  next_number: 1,
  min_length: 0,
  reset_policy: "YEARLY",
  series: 1,
});

const NumberingConfigForm = ({ client_id }) => {
  const [configs, setConfigs] = useState({});
  const [original, setOriginal] = useState({});
  const [loading, setLoading] = useState(false);
  const [savingId, setSavingId] = useState(null);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);
  const [resetTarget, setResetTarget] = useState(null);
  const [resetting, setResetting] = useState(false);

  const fyYear = useMemo(currentFyStartYear, []);
  const fyLabel = `${fyYear}-${String((fyYear + 1) % 100).padStart(2, "0")}`;

  const loadAllConfigs = async () => {
    setLoading(true);
    try {
      const res = await fetchAllNumberingConfigs(client_id);
      const map = {};
      // if several rows exist per type (old FYs / series), keep the latest one
      res.forEach((cfg) => {
        const prev = map[cfg.document_type];
        if (!prev || (cfg.series || 1) >= (prev.series || 1)) {
          map[cfg.document_type] = { ...emptyConfig(cfg.document_type), ...cfg };
        }
      });
      documentTypes.forEach((dt) => {
        if (!map[dt]) map[dt] = emptyConfig(dt);
      });
      setConfigs(map);
      setOriginal(JSON.parse(JSON.stringify(map)));
    } catch (e) {
      setError("Failed to load configs");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (client_id) loadAllConfigs();
    // eslint-disable-next-line
  }, [client_id]);

  // auto-hide success message
  useEffect(() => {
    if (!successMessage) return;
    const t = setTimeout(() => setSuccessMessage(null), 3500);
    return () => clearTimeout(t);
  }, [successMessage]);

  const handleChange = (docType, field, value) => {
    setConfigs((prev) => ({
      ...prev,
      [docType]: { ...prev[docType], [field]: value },
    }));
  };

  const isDirty = (docType) => {
    const a = configs[docType] || {};
    const b = original[docType] || {};
    return ["prefix", "suffix", "next_number", "min_length", "reset_policy"].some(
      (k) => (a[k] ?? "") !== (b[k] ?? "")
    );
  };

  const getProblem = (docType) => {
    const cfg = configs[docType] || {};
    const preview = buildNumber(cfg, fyYear);
    if (GST_LIMIT_TYPES.includes(docType) && preview.length > 16) {
      return `${preview.length} characters. GST allows max 16.`;
    }
    return null;
  };

  const getWarning = (docType) => {
    const a = configs[docType] || {};
    const b = original[docType] || {};
    if (b.next_number && Number(a.next_number) < Number(b.next_number)) {
      return `Lower than current ${b.next_number}. May create duplicates. Use Reset instead.`;
    }
    return null;
  };

  const handleSave = async (docType) => {
    setError(null);
    setSuccessMessage(null);

    if (getProblem(docType)) return;
    if (
      getWarning(docType) &&
      !window.confirm(
        `${getWarning(docType)}\n\nDo you still want to save this ${docType} number?`
      )
    )
      return;

    setSavingId(docType);
    const { prefix, suffix, next_number, min_length, reset_policy } =
      configs[docType] || {};

    try {
      await updateNumberingConfig({
        client_id,
        document_type: docType,
        prefix: prefix || "",
        suffix: suffix || "",
        next_number: next_number || 1,
        min_length: min_length || 0,
        reset_policy: reset_policy || "YEARLY",
      });
      setOriginal((prev) => ({
        ...prev,
        [docType]: JSON.parse(JSON.stringify(configs[docType])),
      }));
      setSuccessMessage(`${docType} numbering saved successfully`);
    } catch (e) {
      setError(e?.message || `Failed to save ${docType}`);
    } finally {
      setSavingId(null);
    }
  };

  const confirmReset = async () => {
    if (!resetTarget) return;
    setResetting(true);
    setError(null);
    try {
      await resetNumberingConfig({
        client_id,
        document_type: resetTarget,
      });
      setSuccessMessage(`${resetTarget} numbering reset to 1`);
      setResetTarget(null);
      await loadAllConfigs();
    } catch (e) {
      setError(e?.message || `Failed to reset ${resetTarget}`);
    } finally {
      setResetting(false);
    }
  };

  if (loading)
    return (
      <div className="p-4 text-sm text-slate-500">Loading configurations...</div>
    );

  const inputCls =
    "w-full h-8 rounded-md border border-slate-400 bg-white px-2 text-[13px] font-medium text-slate-900 " +
    "placeholder:text-slate-400 focus:outline-none focus:border-blue-600 focus:ring-2 focus:ring-blue-600/20 " +
    "disabled:bg-slate-100 disabled:text-slate-500 disabled:cursor-not-allowed";

  const thCls =
    "px-2 py-2 text-left text-[11px] font-semibold uppercase tracking-wide text-slate-600";

  return (
    <div className="w-full max-w-full">
      {/* Header */}
      <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
        <div>
          <h2 className="text-base font-semibold text-slate-900">
            Document numbering
          </h2>
          <p className="text-xs text-slate-500">
            Changes apply from the next document. Existing numbers never change.
          </p>
        </div>
        <span className="rounded-full bg-blue-50 px-3 py-1 text-xs font-semibold text-blue-700">
          Current FY: {fyLabel}
        </span>
      </div>

      {/* Messages */}
      {error && (
        <div className="mb-2 rounded-md border border-red-200 bg-red-50 px-3 py-1.5 text-xs text-red-700">
          {error}
        </div>
      )}
      {successMessage && (
        <div className="mb-2 rounded-md border border-green-200 bg-green-50 px-3 py-1.5 text-xs text-green-700">
          {successMessage}
        </div>
      )}

      {/* Table: fixed widths so it fits the panel; scrolls only on very narrow screens */}
      <div className="overflow-x-auto rounded-lg border border-slate-300 bg-white shadow-sm">
        <table className="w-full min-w-[900px] table-fixed">
          <colgroup>
            <col style={{ width: "12%" }} />
            <col style={{ width: "17%" }} />
            <col style={{ width: "9%" }} />
            <col style={{ width: "10%" }} />
            <col style={{ width: "9%" }} />
            <col style={{ width: "7%" }} />
            <col style={{ width: "21%" }} />
            <col style={{ width: "15%" }} />
          </colgroup>
          <thead className="bg-slate-100">
            <tr>
              <th className={thCls}>Document</th>
              <th className={thCls}>Policy</th>
              <th className={thCls}>Prefix</th>
              <th className={thCls}>Suffix</th>
              <th className={thCls}>Next no.</th>
              <th className={thCls}>Digits</th>
              <th className={thCls}>Next will be</th>
              <th className={`${thCls} text-center`}>Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-200">
            {documentTypes.map((docType) => {
              const cfg = configs[docType] || emptyConfig(docType);
              const policy = cfg.reset_policy || "YEARLY";
              const dirty = isDirty(docType);
              const problem = getProblem(docType);
              const warning = getWarning(docType);
              const policyInfo = POLICIES.find((p) => p.value === policy);
              const preview = buildNumber(cfg, fyYear);

              return (
                <tr
                  key={docType}
                  className={dirty ? "bg-amber-50/60" : "hover:bg-slate-50"}
                >
                  {/* Document */}
                  <td className="px-2 py-2 align-top">
                    <div className="flex h-8 flex-wrap items-center gap-1">
                      <span className="text-[13px] font-semibold text-slate-900">
                        {docType}
                      </span>
                      {cfg.series > 1 && (
                        <span className="rounded bg-slate-100 px-1 text-[10px] font-medium text-slate-600">
                          Series {String.fromCharCode(64 + Number(cfg.series))}
                        </span>
                      )}
                    </div>
                  </td>

                  {/* Policy */}
                  <td className="px-2 py-2 align-top">
                    <select
                      className={inputCls}
                      title={policyInfo?.hint}
                      value={policy}
                      onChange={(e) =>
                        handleChange(docType, "reset_policy", e.target.value)
                      }
                    >
                      {POLICIES.map((p) => (
                        <option key={p.value} value={p.value}>
                          {p.label}
                        </option>
                      ))}
                    </select>
                  </td>

                  {/* Prefix */}
                  <td className="px-2 py-2 align-top">
                    <input
                      type="text"
                      className={inputCls}
                      value={cfg.prefix || ""}
                      maxLength={20}
                      onChange={(e) =>
                        handleChange(docType, "prefix", e.target.value)
                      }
                      placeholder="INV"
                    />
                  </td>

                  {/* Suffix */}
                  <td className="px-2 py-2 align-top">
                    {policy === "NEVER" ? (
                      <input
                        type="text"
                        className={inputCls}
                        value={cfg.suffix || ""}
                        maxLength={20}
                        onChange={(e) =>
                          handleChange(docType, "suffix", e.target.value)
                        }
                        placeholder="optional"
                      />
                    ) : (
                      <input
                        type="text"
                        className={inputCls}
                        value={`Auto -${fyYear}`}
                        title="Year is added automatically from the document date"
                        disabled
                        readOnly
                      />
                    )}
                  </td>

                  {/* Next number */}
                  <td className="px-2 py-2 align-top">
                    <input
                      type="number"
                      className={inputCls}
                      min={1}
                      value={cfg.next_number || 1}
                      onChange={(e) =>
                        handleChange(
                          docType,
                          "next_number",
                          Math.max(1, Number(e.target.value))
                        )
                      }
                    />
                  </td>

                  {/* Padding */}
                  <td className="px-2 py-2 align-top">
                    <input
                      type="number"
                      className={inputCls}
                      min={0}
                      max={10}
                      value={cfg.min_length || 0}
                      onChange={(e) =>
                        handleChange(
                          docType,
                          "min_length",
                          Math.min(10, Math.max(0, Number(e.target.value)))
                        )
                      }
                    />
                  </td>

                  {/* Preview + inline messages */}
                  <td className="px-2 py-2 align-top">
                    <div className="flex h-8 items-center">
                      <span className="truncate rounded-md bg-slate-100 px-2 py-1 font-mono text-[13px] font-semibold text-slate-900">
                        {preview}
                      </span>
                    </div>
                    {problem && (
                      <p className="mt-1 text-[11px] font-medium leading-tight text-red-700">
                        {problem}
                      </p>
                    )}
                    {!problem && warning && (
                      <p className="mt-1 text-[11px] font-medium leading-tight text-amber-800">
                        {warning}
                      </p>
                    )}
                  </td>

                  {/* Actions */}
                  <td className="px-2 py-2 align-top">
                    <div className="flex h-8 items-center justify-center gap-1.5">
                      <button
                        type="button"
                        disabled={savingId === docType || !dirty || !!problem}
                        onClick={() => handleSave(docType)}
                        className="h-8 rounded-md bg-blue-700 px-3 text-[13px] font-semibold text-white hover:bg-blue-800 disabled:cursor-not-allowed disabled:opacity-40"
                      >
                        {savingId === docType ? "..." : dirty ? "Save" : "Saved"}
                      </button>
                      <button
                        type="button"
                        title="Start again from 1 in a new series"
                        onClick={() => setResetTarget(docType)}
                        className="h-8 rounded-md border border-slate-400 bg-white px-2.5 text-[13px] font-semibold text-slate-700 hover:bg-slate-50"
                      >
                        Reset
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Policy help: one compact line */}
      <div className="mt-3 grid gap-x-6 gap-y-1 text-xs text-slate-600 md:grid-cols-3">
        <p>
          <span className="font-semibold text-slate-800">Restart every FY:</span>{" "}
          INV00116-2025 → INV00001-2026
        </p>
        <p>
          <span className="font-semibold text-slate-800">Continue + new year:</span>{" "}
          INV00116-2025 → INV00117-2026
        </p>
        <p>
          <span className="font-semibold text-slate-800">Continue, no year:</span>{" "}
          RV-00020 → RV-00021
        </p>
      </div>

      {/* Reset confirmation */}
      {resetTarget && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 p-4">
          <div className="w-full max-w-sm rounded-xl bg-white p-4 shadow-xl">
            <h3 className="text-sm font-semibold text-slate-900">
              Reset {resetTarget} numbering to 1?
            </h3>
            <p className="mt-2 text-xs text-slate-600">
              Old documents keep their numbers. New numbers start again from 1
              in a new series, so they never repeat an old number.
            </p>
            <p className="mt-2 text-xs text-slate-600">
              Next number will be:{" "}
              <span className="rounded bg-slate-100 px-2 py-0.5 font-mono font-semibold text-slate-900">
                {buildNumber(
                  {
                    ...configs[resetTarget],
                    series: (configs[resetTarget]?.series || 1) + 1,
                  },
                  fyYear,
                  1
                )}
              </span>
            </p>
            <div className="mt-4 flex justify-end gap-2">
              <button
                type="button"
                disabled={resetting}
                onClick={() => setResetTarget(null)}
                className="h-8 rounded-md border border-slate-400 px-3 text-[13px] font-semibold text-slate-700 hover:bg-slate-50"
              >
                Cancel
              </button>
              <button
                type="button"
                disabled={resetting}
                onClick={confirmReset}
                className="h-8 rounded-md bg-red-600 px-3 text-[13px] font-semibold text-white hover:bg-red-700 disabled:opacity-50"
              >
                {resetting ? "Resetting..." : "Yes, reset"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default NumberingConfigForm;